import React, { useCallback, useRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CurrencySelect } from "../currency";
import { LanguageSelect } from "../language";
import { TimezoneSelect } from "../timezone";
import ProductsServicesSelector from "../../feature-module/crm/leads/ProductsServicesSelector";
import { PaginatedAsyncSelect } from "./InfiniteScrollSelect";
import { FormCtx, SelectOption, UniversalFieldConfig } from "./types";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

function resolve<T, R>(
  v: R | ((ctx: FormCtx<T>) => R) | undefined,
  ctx: FormCtx<T>,
  fallback: R
): R {
  if (typeof v === "function") return (v as (ctx: FormCtx<T>) => R)(ctx);
  return v === undefined ? fallback : v;
}

interface FieldRendererProps<T extends Record<string, any>> {
  field: UniversalFieldConfig<T>;
  ctx: FormCtx<T>;
}

function FieldRenderer<T extends Record<string, any>>({
  field,
  ctx,
}: FieldRendererProps<T>) {
  const fieldRef = useRef(field);
  fieldRef.current = field;
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const defaultFormatOptionLabel = (option: any) => {
    const label = option.label || "";
    if (typeof label === "string" && label.length > 30) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-${option.value || Math.random()}`}>{label}</Tooltip>}
        >
          <span>{`${label.substring(0, 30)}...`}</span>
        </OverlayTrigger>
      );
    }
    return label;
  };

  const stableLoadOptions = useCallback(
    (page: number, search: string) =>
      fieldRef.current.loadOptions
        ? fieldRef.current.loadOptions(page, search, ctxRef.current)
        : Promise.resolve({ options: [], hasMore: false }),
    []
  );

  const visible = resolve(field.visible, ctx, true);
  if (!visible) return null;

  if (field.type === "custom") {
    return <>{field.render ? field.render(ctx) : null}</>;
  }

  const label =
    typeof field.label === "function" ? field.label(ctx) : field.label;
  const required = resolve(field.required, ctx, false);
  const readOnly = resolve(field.readOnly, ctx, false);
  const disabled = resolve(field.disabled, ctx, false);
  const value = ctx.values[field.name];
  const errorKey = field.errorKey || field.name;
  const error = ctx.errors[errorKey];
  const colClass = field.colClass || "col-md-6";
  const placeholder =
    typeof field.placeholder === "function"
      ? field.placeholder(ctx)
      : field.placeholder;
  const helperText =
    typeof field.helperText === "function"
      ? field.helperText(ctx)
      : field.helperText;

  const handleChange = (newValue: any) => {
    ctx.setValue(field.name, newValue);
    field.onChange?.(newValue, ctx);
  };

  const wrap = (control: React.ReactNode, isSelectControl = false) => (
    <div className={colClass}>
      <div className={`mb-3 ${isSelectControl && error ? "is-invalid" : ""}`}>
        {label && (
          <label className="form-label">
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
        )}
        {control}
        {helperText && <small className="text-muted d-block mt-1">{helperText}</small>}
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    </div>
  );

  switch (field.type) {
    case "text":
    case "email":
    case "tel":
    case "url":
    case "number": {
      const displayValue = field.format
        ? field.format(value, ctx)
        : value ?? "";
      return wrap(
        <input
          type={field.type}
          className={`form-control ${error ? "is-invalid" : ""}`}
          value={displayValue}
          readOnly={readOnly}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value)}
          {...field.controlProps}
        />
      );
    }

    case "textarea":
      return wrap(
        <textarea
          className={`form-control ${error ? "is-invalid" : ""}`}
          value={value ?? ""}
          readOnly={readOnly}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value)}
          {...field.controlProps}
        />
      );

    case "checkbox":
      return (
        <div className={colClass}>
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={!!value}
              disabled={disabled}
              onChange={(e) => handleChange(e.target.checked)}
              id={`field-${field.name}`}
              {...field.controlProps}
            />
            {label && (
              <label className="form-check-label" htmlFor={`field-${field.name}`}>
                {label}
              </label>
            )}
          </div>
        </div>
      );

    case "date":
      return wrap(
        <input
          type="date"
          className={`form-control ${error ? "is-invalid" : ""}`}
          value={value ?? ""}
          readOnly={readOnly}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value)}
          {...field.controlProps}
        />
      );

    case "datetime":
      return wrap(
        <DatePicker
          selected={value ? new Date(value) : null}
          onChange={(date: Date | null) =>
            handleChange(date ? date.toISOString() : null)
          }
          showTimeSelect
          timeIntervals={15}
          dateFormat="MMM d, yyyy h:mm aa"
          className={`form-control ${error ? "is-invalid" : ""}`}
          disabled={disabled}
          readOnly={readOnly}
          {...field.controlProps}
        />
      );

    case "select": {
      const options = resolve<T, SelectOption[]>(field.options, ctx, []);
      const selected = options.find((o) => o.value === value) || null;
      return wrap(
        <Select
          value={selected}
          options={options}
          onChange={(opt: SelectOption | null) =>
            handleChange(opt ? opt.value : null)
          }
          isClearable={field.isClearable}
          isDisabled={disabled}
          placeholder={placeholder}
          classNamePrefix="react-select"
          formatOptionLabel={field.controlProps?.formatOptionLabel || defaultFormatOptionLabel}
          {...field.controlProps}
        />,
        true
      );
    }

    case "async-select": {
      const dependsOnValue = field.dependsOn ? ctx.values[field.dependsOn] : null;
      const dependsOnKey =
        dependsOnValue && typeof dependsOnValue === "object" && "value" in dependsOnValue
          ? dependsOnValue.value
          : dependsOnValue;
      return wrap(
        <PaginatedAsyncSelect
          key={field.dependsOn ? String(dependsOnKey) : field.name}
          value={
            value && value.value !== undefined
              ? value
              : value !== null && value !== undefined
              ? { value, label: String(value) }
              : null
          }
          onChange={(opt: SelectOption | null) => handleChange(opt)}
          loadOptions={stableLoadOptions}
          isClearable={field.isClearable}
          isDisabled={disabled}
          isMulti={field.isMulti}
          placeholder={placeholder}
          formatOptionLabel={field.controlProps?.formatOptionLabel || defaultFormatOptionLabel}
          {...field.controlProps}
        />,
        true
      );
    }

    case "creatable-multi": {
      const options = resolve<T, SelectOption[]>(field.options, ctx, []);
      const selectedValues: SelectOption[] = Array.isArray(value) ? value : [];
      return wrap(
        <CreatableSelect
          isMulti
          value={selectedValues}
          options={options}
          onChange={(opts: any) => handleChange(opts || [])}
          isDisabled={disabled}
          placeholder={placeholder}
          classNamePrefix="react-select"
          formatOptionLabel={field.controlProps?.formatOptionLabel || defaultFormatOptionLabel}
          {...field.controlProps}
        />,
        true
      );
    }

    case "currency":
      return wrap(
        <CurrencySelect
          value={value}
          onChange={(v) => handleChange(v || "")}
          isDisabled={disabled}
          placeholder={placeholder}
          {...field.controlProps}
        />
      );

    case "language":
      return wrap(
        <LanguageSelect
          value={value}
          onChange={(v) => handleChange(v)}
          isDisabled={disabled}
          placeholder={placeholder}
          {...field.controlProps}
        />
      );

    case "timezone":
      return wrap(
        <TimezoneSelect
          value={value}
          onChange={(v) => handleChange(v)}
          isDisabled={disabled}
          placeholder={placeholder}
          {...field.controlProps}
        />
      );

    case "products-services": {
      const psOpts = field.productsServices;
      if (!psOpts) return null;
      const sumFields = psOpts.sumFields || ["discounted_total"];
      const handleItemsChange = (items: any[]) => {
        ctx.setProductsServices(items);
        const isEmpty = items.length === 0;
        const shouldSkip =
          isEmpty &&
          (psOpts.skipSumWhenEmpty ||
            (psOpts.skipSumWhenEmptyInEditMode && ctx.mode === "edit"));
        if (!shouldSkip && psOpts.writeSumTo) {
          const total = items.reduce(
            (sum: number, item: any) =>
              sum +
              sumFields.reduce((s, f) => s + (item[f] || 0), 0),
            0
          );
          const emptyValue =
            psOpts.emptySumValue !== undefined ? psOpts.emptySumValue : null;
          psOpts.writeSumTo.forEach((key) =>
            ctx.setValue(key, total || emptyValue)
          );
        }
      };
      return (
        <div className={colClass}>
          {label && <label className="form-label">{label}</label>}
          <ProductsServicesSelector
            key={psOpts.remountKey}
            value={ctx.productsServices}
            onChange={handleItemsChange}
            existingItems={psOpts.existingItems}
            isEditMode={ctx.mode === "edit"}
            entityId={psOpts.entityId}
            entityType={psOpts.entityType}
            currencyCode={(ctx.values as any)[psOpts.currencyCodeField || "currency_code"]}
            onRefreshData={psOpts.onRefreshData}
            onUnsavedChanges={psOpts.onUnsavedChanges}
            tenantInfo={psOpts.tenantInfo}
            onDiscountErrors={(hasErrors: boolean) => {
              if (psOpts.gateOnDiscountErrors) ctx.setHasDiscountErrors(hasErrors);
              psOpts.onDiscountErrors?.(hasErrors);
            }}
          />
        </div>
      );
    }

    default:
      return null;
  }
}

export default FieldRenderer;
